// ══════════════════════════════════════════════════════════════════════════════
// PAGE VISIOCONFÉRENCE - Style harmonisé avec Liste de tâches
// AbracadaWorld - Cockpit Particulier
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Video, Plus, Copy, ExternalLink, Clock, Users, Calendar,
  Check, X, Link2, Zap, Share2, Mail, Sparkles, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import RegisterPromptModal from '../../components/RegisterPromptModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getToken = () => {
  const buyerToken = localStorage.getItem('buyer_token');
  if (buyerToken) return buyerToken;
  const clientToken = localStorage.getItem('client_token');
  if (clientToken) return clientToken;
  return localStorage.getItem('authToken');
};

export default function VisioconferencePage({ onAskAbra, onOpenRegisterModal, onBackToHub }) {
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [createdMeeting, setCreatedMeeting] = useState(null);
  const [localShowRegisterModal, setLocalShowRegisterModal] = useState(false);
  const isDarkMode = false;
  
  // Détection mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // État pour détecter le clavier mobile
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  // Détection du clavier via visualViewport API
  useEffect(() => {
    if (!showScheduleModal || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    // Masquer la navbar quand le modal est ouvert
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [showScheduleModal, isMobile]);
  
  const [scheduledMeeting, setScheduledMeeting] = useState({
    summary: '',
    date: '',
    start_time: '09:00',
    duration: '30',
    attendees: ''
  });

  // Vérifier la connexion Google
  const checkGoogleStatus = async () => {
    try {
      const token = getToken();
      if (!token) {
        setGoogleStatus({ connected: false, loading: false });
        return;
      }
      const res = await fetch(`${API_URL}/api/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGoogleStatus({ ...data, loading: false });
        if (data.connected) {
          loadRecentMeetings();
        }
      } else {
        setGoogleStatus({ connected: false, loading: false });
      }
    } catch (error) {
      setGoogleStatus({ connected: false, loading: false });
    }
  };

  // Connecter Google
  const connectGoogle = async () => {
    try {
      const token = getToken();
      
      if (!token) {
        // Ouvrir le popup d'inscription via le parent
        if (onOpenRegisterModal) {
          onOpenRegisterModal('créer une visioconférence Google Meet', true);
        } else {
          setLocalShowRegisterModal(true);
        }
        return;
      }
      
      const res = await fetch(`${API_URL}/api/google/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const { auth_url } = await res.json();
        window.location.href = auth_url;
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Charger les réunions récentes
  const loadRecentMeetings = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/calendar/events?max_results=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const meetingsWithLink = (data.events || []).filter(e => e.meet_link);
        setRecentMeetings(meetingsWithLink);
      }
    } catch (error) {
      console.error('Error loading meetings:', error);
    }
  };

  // Créer une réunion instantanée
  const createInstantMeeting = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/meet/instant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCreatedMeeting(data.event);
        loadRecentMeetings();
        toast.success('Réunion créée !');
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Planifier une réunion
  const scheduleNewMeeting = async () => {
    if (!scheduledMeeting.summary || !scheduledMeeting.date) {
      toast.error('Le titre et la date sont obligatoires');
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      
      const startDateTime = `${scheduledMeeting.date}T${scheduledMeeting.start_time}:00`;
      const attendeesList = scheduledMeeting.attendees
        .split(',')
        .map(e => e.trim())
        .filter(e => e.includes('@'));
      
      const res = await fetch(`${API_URL}/api/google/meet/schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          summary: scheduledMeeting.summary,
          start_datetime: startDateTime,
          duration_minutes: parseInt(scheduledMeeting.duration),
          attendees: attendeesList
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setCreatedMeeting(data.event);
        setShowScheduleModal(false);
        setScheduledMeeting({
          summary: '',
          date: '',
          start_time: '09:00',
          duration: '30',
          attendees: ''
        });
        loadRecentMeetings();
        toast.success('Visioconférence planifiée !');
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Copier le lien
  const copyMeetLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Lien copié !');
  };

  // Partager par email
  const shareByEmail = (meeting) => {
    const subject = encodeURIComponent(`Invitation: ${meeting.summary}`);
    const body = encodeURIComponent(`Bonjour,\n\nJe t'invite à rejoindre cette visioconférence:\n\n${meeting.meet_link}\n\nÀ bientôt !`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  useEffect(() => {
    checkGoogleStatus();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ════════════════════════════════════════════════════════════════════════════
  // ÉTAT NON CONNECTÉ - Style harmonisé
  // ════════════════════════════════════════════════════════════════════════════
  if (!googleStatus.loading && !googleStatus.connected) {
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className={isMobile ? 'mb-2' : 'mb-4'}>
          <div className="flex items-center gap-2">
            {isMobile && onBackToHub && (
              <button onClick={onBackToHub} className="p-1 -ml-1" data-testid="back-to-hub-btn">
                <ArrowLeft size={20} className="text-[#1e3a5f]" strokeWidth={2} />
              </button>
            )}
            <div>
              <h1 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-bold text-[#1e3a5f]`}>Visioconférence</h1>
              {!isMobile && <p className="text-sm text-gray-500">Crée des réunions vidéo en un clic et partage le lien</p>}
            </div>
          </div>
        </div>

        {/* État non connecté */}
        <div className="flex-1 rounded-2xl bg-white border border-gray-100 flex items-center justify-center">
          <div className="text-center p-8 max-w-md">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold mb-4">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
              Version Beta
            </div>
            
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-orange-100 to-orange-50">
              <Video size={28} className="text-orange-500" />
            </div>
            
            <h3 className="text-lg font-semibold mb-2 text-gray-900">Crée des visioconférences</h3>
            <p className="text-sm mb-6 text-gray-500">
              Connecte ton compte pour créer des réunions Google Meet.
            </p>
            
            {/* Google Meet */}
            <button
              onClick={connectGoogle}
              data-testid="connect-meet-btn"
              className="w-full py-3 rounded-xl flex items-center justify-center gap-3 transition-colors mb-3 bg-white hover:bg-gray-50 border-2 border-gray-200"
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
              <span className="font-semibold text-sm text-gray-700">Google Meet</span>
              <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Disponible</span>
            </button>
            
            {/* Microsoft Teams */}
            <div className="w-full py-3 rounded-xl flex items-center justify-center gap-3 mb-3 opacity-60 bg-gray-50 border-2 border-dashed border-gray-200">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7v10l10 5 10-5V7L12 2z" fill="#5059C9"/>
              </svg>
              <span className="font-semibold text-sm text-gray-500">Microsoft Teams</span>
              <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Bientôt</span>
            </div>
            
            {/* FaceTime */}
            <div className="w-full py-3 rounded-xl flex items-center justify-center gap-3 opacity-60 bg-gray-50 border-2 border-dashed border-gray-200">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="4" width="16" height="14" rx="2" fill="#34C759"/>
                <path d="M18 9l4-2v8l-4-2V9z" fill="#34C759"/>
              </svg>
              <span className="font-semibold text-sm text-gray-500">FaceTime</span>
              <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Bientôt</span>
            </div>
            
            <p className="mt-4 text-xs text-gray-400">Tes données restent privées et sécurisées.</p>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // ÉTAT CONNECTÉ - Style harmonisé
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div className="h-full flex flex-col">
      {/* Header - Style harmonisé */}
      <div className={isMobile ? 'mb-2' : 'mb-4'}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isMobile && onBackToHub && (
              <button onClick={onBackToHub} className="p-1 -ml-1" data-testid="back-to-hub-btn">
                <ArrowLeft size={20} className="text-[#1e3a5f]" strokeWidth={2} />
              </button>
            )}
            <div>
              <h1 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-bold text-[#1e3a5f]`}>Visioconférence</h1>
              {!isMobile && <p className="text-sm text-gray-500">Google Meet connecté</p>}
            </div>
          </div>
          {isMobile ? (
            <button 
              onClick={() => setShowScheduleModal(true)}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-[#F97316] text-white rounded-lg font-semibold text-[11px] whitespace-nowrap"
            >
              Nouvelle visio
            </button>
          ) : (
            <Button 
              onClick={() => setShowScheduleModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
            >
              <Plus size={16} />
              Planifier
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-100">
            <Video size={14} className="text-orange-500" />
            <span className="text-xs font-medium text-gray-600">
              {recentMeetings.length} réunion{recentMeetings.length > 1 ? 's' : ''} récente{recentMeetings.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Bouton réunion instantanée */}
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={createInstantMeeting}
        disabled={loading}
        className="w-full p-4 mb-4 rounded-2xl text-white shadow-md transition-all hover:shadow-lg"
        style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
            <Zap size={24} />
          </div>
          <div className="flex-1 text-left">
            <h3 className="text-base font-bold">Réunion instantanée</h3>
            <p className="text-white/80 text-sm">Créer un lien Meet maintenant</p>
          </div>
          {loading ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Plus size={22} />
          )}
        </div>
      </motion.button>

      {/* Meeting créé */}
      <AnimatePresence>
        {createdMeeting && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-4 rounded-2xl bg-green-50 border border-green-200"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
                  <Check size={16} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-green-800 text-sm">Réunion créée !</p>
                  <p className="text-xs text-green-600">{createdMeeting.summary}</p>
                </div>
              </div>
              <button onClick={() => setCreatedMeeting(null)} className="p-1 hover:bg-green-100 rounded-full">
                <X size={16} className="text-green-600" />
              </button>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => copyMeetLink(createdMeeting.meet_link)}
                variant="outline"
                size="sm"
                className="flex-1 gap-2 rounded-xl border-green-300 text-green-700 hover:bg-green-100"
              >
                <Copy size={14} />
                Copier le lien
              </Button>
              <Button
                onClick={() => window.open(createdMeeting.meet_link, '_blank')}
                size="sm"
                className="flex-1 gap-2 rounded-xl bg-green-600 hover:bg-green-700 text-white"
              >
                <ExternalLink size={14} />
                Rejoindre
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Liste des réunions */}
      <div className="flex-1 rounded-2xl bg-white border border-gray-100 overflow-hidden">
        {recentMeetings.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gray-50">
                <Video size={28} className="text-gray-300" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">Aucune réunion récente</h3>
              <p className="text-sm text-gray-500">Crée ta première visioconférence !</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 overflow-y-auto h-full">
            {recentMeetings.map((meeting) => (
              <div key={meeting.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Video size={18} className="text-orange-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-gray-900 truncate">{meeting.summary}</h4>
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <Clock size={12} />
                      {formatDate(meeting.start)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => copyMeetLink(meeting.meet_link)}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      title="Copier le lien"
                    >
                      <Copy size={16} className="text-gray-500" />
                    </button>
                    <button
                      onClick={() => shareByEmail(meeting)}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      title="Partager par email"
                    >
                      <Mail size={16} className="text-gray-500" />
                    </button>
                    <a
                      href={meeting.meet_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg hover:bg-orange-50 transition-colors"
                      title="Rejoindre"
                    >
                      <ExternalLink size={16} className="text-orange-500" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal planifier - Version mobile plein écran / Desktop centré */}
      <AnimatePresence>
        {showScheduleModal && (
          isMobile ? (
            /* ═══ VERSION MOBILE - PLEIN ÉCRAN ═══ */
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="fixed inset-0 z-[10002] flex flex-col bg-white"
            >
              {/* Header */}
              <header className="flex items-center gap-2 px-3 py-3 border-b border-gray-100">
                <button 
                  onClick={() => setShowScheduleModal(false)}
                  className="p-1 text-gray-500"
                >
                  <ArrowLeft size={22} />
                </button>
                <h1 className="text-[15px] font-bold text-gray-900">Planifier une visio</h1>
              </header>

              {/* Contenu scrollable */}
              <div className="flex-1 overflow-y-auto p-5">
                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Titre *</label>
                  <input
                    type="text"
                    value={scheduledMeeting.summary}
                    onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, summary: e.target.value })}
                    placeholder="Ex: Point hebdomadaire"
                    className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Date *</label>
                    <input
                      type="date"
                      value={scheduledMeeting.date}
                      onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, date: e.target.value })}
                      className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Heure</label>
                    <input
                      type="time"
                      value={scheduledMeeting.start_time}
                      onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, start_time: e.target.value })}
                      className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Durée</label>
                  <select
                    value={scheduledMeeting.duration}
                    onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, duration: e.target.value })}
                    className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">1 heure</option>
                    <option value="90">1h30</option>
                    <option value="120">2 heures</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Participants</label>
                  <input
                    type="text"
                    value={scheduledMeeting.attendees}
                    onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, attendees: e.target.value })}
                    placeholder="email1@example.com, email2@example.com"
                    className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                  />
                </div>
              </div>

              {/* Footer fixe */}
              <div 
                className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
                style={{ 
                  paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
                  marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
                }}
              >
                <button
                  onClick={() => {
                    if (onAskAbra) {
                      onAskAbra({ title: scheduledMeeting.summary, date: scheduledMeeting.date, time: scheduledMeeting.time, participants: scheduledMeeting.participants, isNew: true });
                      setShowScheduleModal(false);
                    } else {
                      toast.info('Demande à Abra de planifier ta visio !');
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
                >
                  <Sparkles size={16} />
                  Créer avec Abracada
                </button>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowScheduleModal(false)}
                    className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={scheduleNewMeeting}
                    disabled={loading || !scheduledMeeting.summary || !scheduledMeeting.date}
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50"
                  >
                    {loading ? 'Création...' : 'Créer'}
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            /* ═══ VERSION DESKTOP ═══ */
            <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
              >
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <h3 className="text-lg font-semibold text-gray-900">Planifier une visio</h3>
                  <button onClick={() => setShowScheduleModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Titre de la réunion *</label>
                    <input
                      type="text"
                      value={scheduledMeeting.summary}
                      onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, summary: e.target.value })}
                      placeholder="Ex: Point hebdomadaire"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Date *</label>
                      <input
                        type="date"
                        value={scheduledMeeting.date}
                        onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, date: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Heure</label>
                      <input
                        type="time"
                        value={scheduledMeeting.start_time}
                        onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, start_time: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Durée</label>
                    <select
                      value={scheduledMeeting.duration}
                      onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, duration: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">1 heure</option>
                      <option value="90">1h30</option>
                      <option value="120">2 heures</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Participants (emails séparés par des virgules)
                    </label>
                    <input
                      type="text"
                      value={scheduledMeeting.attendees}
                      onChange={(e) => setScheduledMeeting({ ...scheduledMeeting, attendees: e.target.value })}
                      placeholder="email1@example.com, email2@example.com"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowScheduleModal(false)}>
                      Annuler
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        if (onAskAbra) {
                          onAskAbra({ title: scheduledMeeting.summary, date: scheduledMeeting.date, time: scheduledMeeting.time, participants: scheduledMeeting.participants, isNew: true });
                          setShowScheduleModal(false);
                        } else {
                          toast.info('Demande à Abra de planifier ta visio !');
                        }
                      }}
                      className="px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2"
                      style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
                    >
                      <Sparkles size={16} />
                      Demander à Abra
                    </button>
                    <Button
                      onClick={scheduleNewMeeting}
                      disabled={loading || !scheduledMeeting.summary || !scheduledMeeting.date}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      {loading ? 'Création...' : 'Créer la visio'}
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
          )
        )}
      </AnimatePresence>

      {/* Modal d'inscription contextuel fallback (si parent ne gère pas) */}
      {!onOpenRegisterModal && (
        <RegisterPromptModal 
          isOpen={localShowRegisterModal} 
          onClose={() => setLocalShowRegisterModal(false)}
          actionLabel="créer des visioconférences Google Meet"
          showGoogleOption={true}
        />
      )}
    </div>
  );
}
